import { useEffect, useMemo, useRef, useState } from 'react';

import { useI18n } from '../i18n/I18nProvider';
import ImageDrop from './ImageDrop';
import AIRewrite from './AIRewrite';
import InstagramBrandKitLivePreview from './InstagramBrandKitLivePreview';
import useIsMobile from './useIsMobile';
import { buildPollinationsImageUrl } from '../imagegen/pollinationsClient';
import {
  canvasToPngDataUrl,
  downloadDataUrl,
  fontFamilyForBrand,
  renderHighlightCover,
  renderProfileAvatar,
} from '../brandkit/brandKitRender';
import { generateBrandLogoDataUrl, generateHighlightIconDataUrl } from '../brandkit/iconGen';
import { contrastRatio, ensureBrandColorSystem, makeColorSystemFromPalette, normalizeHex, pickReadableText } from '../brandkit/colorSystem';

function normalizeHexColor(input) {
  const raw = String(input || '').trim();
  if (!raw) return '';
  const s = raw.startsWith('#') ? raw.slice(1) : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(s)) return '';
  return `#${s.toUpperCase()}`;
}

function clampNumber(value, { min, max }) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
}

function loadSectionOpenState() {
  try {
    const raw = localStorage.getItem('brand_kit_sections_v1') || '';
    const obj = JSON.parse(raw);
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
}

function saveSectionOpenState(next) {
  try {
    localStorage.setItem('brand_kit_sections_v1', JSON.stringify(next || {}));
  } catch {
    // ignore
  }
}

function AccordionCard({ id, title, defaultOpen = true, children, openState, onToggle }) {
  const open = typeof openState?.[id] === 'boolean' ? openState[id] : defaultOpen;
  return (
    <details
      className="Card Card--accordion"
      open={open}
    >
      <summary
        className="Card-title Card-title--summary"
        onClick={(e) => {
          // Use controlled toggle to avoid browser/native toggle fighting React state.
          e.preventDefault();
          onToggle?.(id, !open);
        }}
      >
        {title}
      </summary>
      <div className="Card-body">{children}</div>
    </details>
  );
}

function StepPillButton({ active, done, label, index, onClick }) {
  const cls = ['StepPillBtn', active ? 'StepPill--active' : '', done ? 'StepPill--done' : ''].filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} onClick={onClick} aria-current={active ? 'step' : undefined}>
      <span className="StepPillIndex" aria-hidden="true">
        {done ? '✓' : index}
      </span>
      <span className="StepPillLabel">{label}</span>
    </button>
  );
}

export default function BrandKitPage({
  brands,
  activeBrand,
  activeBrandId,
  onSetActiveBrandId,
  onCreateBrand,
  onUpdateActiveBrand,
  onUploadLogo,
  onClearLogo,
  apiKey,
  useProxy,
}) {
  const { t, locale } = useI18n();
  const mobile = useIsMobile(1100);
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [newColor, setNewColor] = useState('#F5C55B');
  const [colorMode, setColorMode] = useState('dark'); // dark | light
  const [highlightLabels, setHighlightLabels] = useState(() => ['Menu', 'Offers', 'Reviews', 'Hours', 'Location', 'Contact']);
  const [generateEnglish, setGenerateEnglish] = useState(true);
  const [generateArabic, setGenerateArabic] = useState(true);
  const [regenerateIcons, setRegenerateIcons] = useState(false);
  const [highlightArtStyle, setHighlightArtStyle] = useState('illustration'); // illustration | sub_logo
  const [includeHighlights, setIncludeHighlights] = useState(true);
  const [includeAvatar, setIncludeAvatar] = useState(true);
  const [includePosts, setIncludePosts] = useState(true);
  const [postCount, setPostCount] = useState(9);
  const [postTopicsText, setPostTopicsText] = useState(() => 'New menu item\nBehind the scenes\nSpecial offer\nCustomer review\nChef pick\nLocation vibes\nDelivery highlight\nSignature dish\nWeekend promo');
  const [isGeneratingPosts, setIsGeneratingPosts] = useState(false);
  const [postsOp, setPostsOp] = useState({ status: 'idle', message: '', index: 0, total: 0 }); // idle|generating|loading|done|error
  const [isBuildingPack, setIsBuildingPack] = useState(false);
  const [isPreviewingPack, setIsPreviewingPack] = useState(false);
  const [isGeneratingLogo, setIsGeneratingLogo] = useState(false);
  const [assets, setAssets] = useState([]); // [{id, kind, label, dataUrl, filename}]
  const [packPreviewAssets, setPackPreviewAssets] = useState([]); // same shape as assets, preview-only
  const [iconBackdropMode, setIconBackdropMode] = useState('transparent'); // transparent | surface | primary | accent | custom
  const [iconBackdropColor, setIconBackdropColor] = useState('#000000');
  const [iconBackdropOpacity, setIconBackdropOpacity] = useState(0.35);
  const [openSections, setOpenSections] = useState(() => loadSectionOpenState());
  const igPickInputRef = useRef(null);
  const [igPickTarget, setIgPickTarget] = useState(null); // { kind: 'avatar'|'highlight'|'grid', id?: string, index?: number }
  const [igEditTarget, setIgEditTarget] = useState(null); // same shape as igPickTarget
  const [igEditPromptMode, setIgEditPromptMode] = useState('preset'); // preset | custom
  const [igEditPreset, setIgEditPreset] = useState('default'); // default | minimal | illustration | luxury | playful
  const [igEditPrompt, setIgEditPrompt] = useState(''); // custom prompt
  const [igAiBusy, setIgAiBusy] = useState(false);
  const [igOp, setIgOp] = useState({ status: 'idle', message: '' }); // idle|generating|loading|done|error
  const [igSheetOpen, setIgSheetOpen] = useState(false);
  const [bkSettingsTab, setBkSettingsTab] = useState('identity'); // identity | pack

  useEffect(() => {
    if (!error) return;
    const id = window.setTimeout(() => setError(''), 9000);
    return () => window.clearTimeout(id);
  }, [error]);

  // NOTE: IG editor is inline (not modal), so we avoid body scroll locking here.
  useEffect(() => {
    if (!igEditTarget) return;
    setIgSheetOpen(true);
  }, [igEditTarget]);

  // Mobile bottom-sheet: lock page scroll while the sheet is open so swipe/scroll stays inside the sheet.
  useEffect(() => {
    const cls = 'BKSheetOpen';
    if (typeof window === 'undefined') return () => {};

    const mq = window.matchMedia ? window.matchMedia('(max-width: 980px)') : null;
    const apply = () => {
      const shouldLock = Boolean(igSheetOpen) && Boolean(mq ? mq.matches : false);
      try {
        document.body.classList.toggle(cls, shouldLock);
      } catch {
        // ignore
      }
    };

    apply();

    if (!mq) {
      return () => {
        try {
          document.body.classList.remove(cls);
        } catch {
          // ignore
        }
      };
    }

    const onChange = () => apply();

    try {
      mq.addEventListener('change', onChange);
      return () => {
        mq.removeEventListener('change', onChange);
        try {
          document.body.classList.remove(cls);
        } catch {
          // ignore
        }
      };
    } catch {
      // Safari fallback
      mq.addListener(onChange);
      return () => {
        mq.removeListener(onChange);
        try {
          document.body.classList.remove(cls);
        } catch {
          // ignore
        }
      };
    }
  }, [igSheetOpen]);

  const toggleSection = (id, isOpen) => {
    setOpenSections((prev) => {
      const next = { ...(prev || {}), [id]: Boolean(isOpen) };
      saveSectionOpenState(next);
      return next;
    });
  };

  async function onGenerateLogo() {
    if (!activeBrand) return;
    if (!useProxy && !String(apiKey || '').trim()) return;

    setIsGeneratingLogo(true);
    try {
      const res = await generateBrandLogoDataUrl({
        brandName: activeBrand.name || '',
        industry: activeBrand.industry || '',
        tone: activeBrand.tone || '',
        palette,
        apiKey,
        useProxy,
        seed: undefined,
      });

      if (!res?.dataUrl) return;
      onUpdateActiveBrand?.({
        logoDataUrl: res.dataUrl,
        logoDataUrlSmall: res.dataUrl,
        logoName: 'ai-logo.png',
      });
    } catch (e) {
      setError(`${t('errors.brandKitLogoGenerateFailed')} ${String(e)}`);
    } finally {
      setIsGeneratingLogo(false);
    }
  }

  const steps = useMemo(
    () => [
      { id: 1, label: t('brandKit.steps.basics') },
      { id: 2, label: t('brandKit.steps.studio') },
      { id: 3, label: t('brandKit.steps.export') },
    ],
    [t],
  );

  const hasLogo = Boolean(activeBrand?.logoDataUrlSmall || activeBrand?.logoDataUrl);
  const palette = activeBrand?.palette || [];
  const igLive = (activeBrand?.brandKitIgLivePreview && typeof activeBrand.brandKitIgLivePreview === 'object')
    ? activeBrand.brandKitIgLivePreview
    : {};
  const brandFontFamily = useMemo(
    () =>
      fontFamilyForBrand({
        fontArabic: activeBrand?.fontArabic,
        fontLatin: activeBrand?.fontLatin,
        locale,
      }),
    [activeBrand?.fontArabic, activeBrand?.fontLatin, locale],
  );

  const colorSystem = useMemo(() => ensureBrandColorSystem(activeBrand) || null, [activeBrand]);
  const roles = colorSystem?.[colorMode]?.roles || null;
  const locks = colorSystem?.[colorMode]?.locks || null;

  function rolePreviewForKey(key) {
    const k = String(key || '').trim();
    const get = (v) => normalizeHex(v) || '';
    const roleHex = get(roles?.[k]);
    const bg = get(roles?.background) || '#070503';
    const surface = get(roles?.surface) || bg;
    const primary = get(roles?.primary) || '#B77A2A';

    if (k === 'text' || k === 'textMuted') {
      return { bg, fg: roleHex || pickReadableText(bg), border: '' };
    }
    if (k === 'border') {
      return { bg: surface, fg: pickReadableText(surface), border: roleHex || '' };
    }
    if (k === 'textOnPrimary') {
      return { bg: primary, fg: roleHex || pickReadableText(primary), border: '' };
    }
    const swatchBg = roleHex || surface;
    return { bg: swatchBg, fg: pickReadableText(swatchBg), border: '' };
  }

  useEffect(() => {
    if (!activeBrand) return;
    if (activeBrand.colorSystem?.dark?.roles && activeBrand.colorSystem?.light?.roles) return;
    const next = ensureBrandColorSystem(activeBrand);
    if (!next) return;
    onUpdateActiveBrand?.({ colorSystem: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrand?.id]);

  useEffect(() => {
    const cfg = activeBrand?.highlightIconBackdrop;
    if (!cfg || typeof cfg !== 'object') return;
    if (typeof cfg.mode === 'string') setIconBackdropMode(cfg.mode);
    if (typeof cfg.color === 'string') setIconBackdropColor(cfg.color);
    if (typeof cfg.opacity === 'number') setIconBackdropOpacity(cfg.opacity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrand?.id]);

  function saveIconBackdrop(next) {
    if (!activeBrand) return;
    onUpdateActiveBrand?.({ highlightIconBackdrop: next });
  }

    function computedBackdrop() {
      if (!roles) return { color: '', opacity: 0 };
      const mode = iconBackdropMode;
      if (mode === 'surface') return { color: roles.surface, opacity: iconBackdropOpacity };
      if (mode === 'primary') return { color: roles.primary, opacity: iconBackdropOpacity };
      if (mode === 'accent') return { color: roles.accent, opacity: iconBackdropOpacity };
      if (mode === 'custom') return { color: normalizeHex(iconBackdropColor) || '', opacity: iconBackdropOpacity };
      return { color: '', opacity: 0 };
    }

    useEffect(() => {
      let cancelled = false;

      async function run() {
        if (step !== 2) return;
        if (!activeBrand) return;

        setIsPreviewingPack(true);
        try {
          const next = [];
          const iconBackdrop = computedBackdrop();
          const logoDataUrl = activeBrand.logoDataUrlSmall || activeBrand.logoDataUrl || '';

          if (includeAvatar) {
            const c = await renderProfileAvatar({
              size: 512,
              palette,
              colorSystem,
              colorMode,
              logoDataUrl,
              brandName: activeBrand.name,
              fontFamily: brandFontFamily,
            });
            const dataUrl = await canvasToPngDataUrl(c);
            if (dataUrl) {
              next.push({
                id: 'preview-avatar',
                kind: 'avatar',
                label: t('brandKit.assets.avatar'),
                dataUrl,
                filename: '',
              });
            }
          }

          if (includeHighlights) {
            const items = [];
            for (const raw of highlightLabels) {
              const { en, ar, key } = parseBilingualLabel(raw);
              if (!key) continue;
              if (generateEnglish && en) items.push({ id: `en:${key}:${en}`, key, label: en });
              if (generateArabic && ar) items.push({ id: `ar:${key}:${ar}`, key, label: ar });
              if (items.length >= 6) break;
            }

            const lib =
              activeBrand.highlightIcons && typeof activeBrand.highlightIcons === 'object' ? activeBrand.highlightIcons : {};

            for (const it of items) {
              const existing = lib?.[iconCacheKey(it.key)] || null;
              const iconDataUrl = existing?.dataUrl || '';
              const c = await renderHighlightCover({
                size: 512,
                palette,
                colorSystem,
                colorMode,
                label: it.label,
                iconDataUrl,
                iconBackdrop,
                fontFamily: brandFontFamily,
              });
              const dataUrl = await canvasToPngDataUrl(c);
              if (dataUrl) {
                next.push({
                  id: `preview-highlight:${it.id}`,
                  kind: 'highlight',
                  label: it.label,
                  dataUrl,
                  filename: '',
                });
              }
            }
          }

          if (!cancelled) setPackPreviewAssets(next);
        } finally {
          if (!cancelled) setIsPreviewingPack(false);
        }
      }

      run().catch(() => {
        if (!cancelled) setIsPreviewingPack(false);
      });

      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      step,
      activeBrand?.id,
      activeBrand?.highlightIcons,
      activeBrand?.logoDataUrlSmall,
      activeBrand?.logoDataUrl,
      includeAvatar,
      includeHighlights,
      generateEnglish,
      generateArabic,
      highlightLabels,
      palette,
      colorSystem,
      colorMode,
      brandFontFamily,
      iconBackdropMode,
      iconBackdropColor,
      iconBackdropOpacity,
      highlightArtStyle,
      t,
    ]);
  
    function setPalette(nextPalette) {
      const next = (nextPalette || [])
        .map((c) => normalizeHexColor(c))
      .filter(Boolean);
    const uniq = Array.from(new Set(next));
    onUpdateActiveBrand?.({ palette: uniq });
  }

  function updatePaletteColor(index, hex) {
    const nextHex = normalizeHexColor(hex);
    if (!nextHex) return;
    const next = [...palette];
    next[index] = nextHex;
    setPalette(next);
  }

  function removePaletteColor(index) {
    const next = palette.filter((_, i) => i !== index);
    setPalette(next);
  }

  function addPaletteColor() {
    const nextHex = normalizeHexColor(newColor);
    if (!nextHex) return;
    if (palette.includes(nextHex)) return;
    setPalette([...palette, nextHex]);
  }

  function setColorSystem(next) {
    if (!activeBrand) return;
    onUpdateActiveBrand?.({ colorSystem: next });
  }

  function regenColorSystemFromPalette() {
    const next = {
      dark: makeColorSystemFromPalette(palette, 'dark'),
      light: makeColorSystemFromPalette(palette, 'light'),
    };
    setColorSystem(next);
  }

  function updateRole(roleKey, value) {
    if (!colorSystem) return;
    const hex = normalizeHex(value);
    if (!hex) return;
    const next = structuredClone ? structuredClone(colorSystem) : JSON.parse(JSON.stringify(colorSystem));
    const bucket = next[colorMode];
    if (!bucket?.roles) return;
    bucket.roles[roleKey] = hex;
    if (roleKey === 'background' && bucket.locks?.text) {
      bucket.roles.text = pickReadableText(hex);
    }
    if (roleKey === 'primary' && bucket.locks?.textOnPrimary) {
      bucket.roles.textOnPrimary = pickReadableText(hex);
    }
    setColorSystem(next);
  }

  function toggleLock(roleKey) {
    if (!colorSystem) return;
    const next = structuredClone ? structuredClone(colorSystem) : JSON.parse(JSON.stringify(colorSystem));
    const bucket = next[colorMode];
    if (!bucket?.locks) return;
    bucket.locks[roleKey] = !bucket.locks[roleKey];
    setColorSystem(next);
  }

  const contrastBg = roles ? contrastRatio(roles.text, roles.background) : 1;
  const contrastSurface = roles ? contrastRatio(roles.text, roles.surface) : 1;
  const contrastPrimary = roles ? contrastRatio(roles.textOnPrimary, roles.primary) : 1;

  function badgeForContrast(r) {
    const v = Number(r);
    if (!Number.isFinite(v)) return { label: '—', variant: 'warn' };
    if (v >= 7) return { label: 'AAA', variant: 'ok' };
    if (v >= 4.5) return { label: 'AA', variant: 'ok' };
    if (v >= 3) return { label: 'Large', variant: 'warn' };
    return { label: 'Low', variant: 'bad' };
  }

  function updateLabel(index, value) {
    const next = [...highlightLabels];
    next[index] = String(value || '').slice(0, 24);
    setHighlightLabels(next);
  }

  function addLabel() {
    setHighlightLabels((prev) => [...prev, 'New']);
  }

  function removeLabel(index) {
    setHighlightLabels((prev) => prev.filter((_, i) => i !== index));
  }

  function normalizeIconKey(s) {
    return String(s || '').trim().toLowerCase().replace(/\s+/g, ' ').slice(0, 80);
  }

  function iconCacheKey(baseKey) {
    const base = normalizeIconKey(baseKey);
    const style = highlightArtStyle === 'sub_logo' ? 'sub_logo' : 'illustration';
    return `${base}::${style}`;
  }

  function parseBilingualLabel(raw) {
    const s = String(raw || '').trim();
    if (!s) return { en: '', ar: '', key: '' };
    const parts = s.split('|').map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const en = parts[0];
      const ar = parts.slice(1).join(' | ');
      return { en, ar, key: normalizeIconKey(en || ar) };
    }
    // No explicit pairing; use the same for both.
    return { en: s, ar: s, key: normalizeIconKey(s) };
  }

  async function getOrGenerateIcon({ en, ar, key }) {
    if (!activeBrand) return { dataUrl: '', prompt: '' };
    const lib = (activeBrand.highlightIcons && typeof activeBrand.highlightIcons === 'object') ? activeBrand.highlightIcons : {};
    const cacheKey = iconCacheKey(key);
    const existing = lib?.[cacheKey];
    if (!regenerateIcons && existing?.dataUrl) return existing;

    if (!useProxy && !String(apiKey || '').trim()) {
      return { dataUrl: '', prompt: '' };
    }

    const referenceImage = !useProxy ? (activeBrand?.logoDataUrlSmall || activeBrand?.logoDataUrl || '') : '';
    const style = highlightArtStyle === 'sub_logo' ? 'sub_logo' : 'mini_illustration';

    let res = null;
    try {
      res = await generateHighlightIconDataUrl({
        conceptEn: en,
        conceptAr: ar,
        palette,
        brandName: activeBrand?.name || '',
        industry: activeBrand?.industry || '',
        tone: activeBrand?.tone || '',
        style,
        apiKey,
        useProxy,
        seed: undefined,
        referenceImage,
      });
    } catch (e) {
      setError(`${t('errors.brandKitIconGenerateFailed')} ${String(e)}`);
      return { dataUrl: '', prompt: '' };
    }

    const nextLib = { ...lib, [cacheKey]: { dataUrl: res.dataUrl, prompt: res.prompt, updatedAt: new Date().toISOString() } };
    onUpdateActiveBrand?.({ highlightIcons: nextLib });
    return nextLib[cacheKey];
  }

  function getCachedIcon(key) {
    if (!activeBrand) return null;
    const lib = (activeBrand.highlightIcons && typeof activeBrand.highlightIcons === 'object') ? activeBrand.highlightIcons : {};
    return lib?.[key] || null;
  }

  function openIgPick(target) {
    setIgPickTarget(target || null);
    try {
      igPickInputRef.current?.click?.();
    } catch {
      // ignore
    }
  }

  function startIgUpload(target) {
    if (!target) return;
    openIgPick(target);
  }

  function saveIgLive(next) {
    if (!activeBrand) return;
    onUpdateActiveBrand?.({ brandKitIgLivePreview: next && Object.keys(next).length ? next : null });
  }

  function openIgEdit(target) {
    setIgEditTarget(target || null);
    const key = keyForIgTarget(target);
    const meta = key && igLive?.promptsMeta && typeof igLive.promptsMeta === 'object' ? igLive.promptsMeta[key] : null;
    const mode = String(meta?.mode || '').trim();
    const preset = String(meta?.preset || '').trim();
    const custom = String(meta?.customPrompt || '').trim();
    setIgEditPromptMode(mode === 'custom' ? 'custom' : 'preset');
    setIgEditPreset(preset || 'default');
    setIgEditPrompt(custom);
    setIgOp({ status: 'idle', message: '' });
  }

  function closeIgEdit() {
    setIgEditTarget(null);
    setIgOp({ status: 'idle', message: '' });
  }

  function keyForIgTarget(target) {
    if (!target) return '';
    if (target.kind === 'avatar') return 'avatar';
    if (target.kind === 'profileName') return 'profileName';
    if (target.kind === 'bio') return 'bio';
    if (target.kind === 'highlight' && target.id) return `highlight:${target.id}`;
    if (target.kind === 'grid' && Number.isFinite(Number(target.index))) return `grid:${Number(target.index)}`;
    if (target.kind === 'caption' && Number.isFinite(Number(target.index))) return `caption:${Number(target.index)}`;
    return '';
  }

  const igLiveAvatarDataUrl = useMemo(() => {
    const override = String(igLive?.avatarDataUrl || '').trim();
    if (override) return override;
    const pv = (packPreviewAssets || []).find((a) => a.kind === 'avatar');
    return pv?.dataUrl || activeBrand?.logoDataUrlSmall || activeBrand?.logoDataUrl || '';
  }, [activeBrand?.logoDataUrl, activeBrand?.logoDataUrlSmall, igLive?.avatarDataUrl, packPreviewAssets]);

  const igLiveHighlights = useMemo(() => {
    const map = igLive?.highlights && typeof igLive.highlights === 'object' ? igLive.highlights : {};
    const base = (packPreviewAssets || []).filter((a) => a.kind === 'highlight').slice(0, 6);
    return base.map((a, idx) => ({
      id: a.id || `hl-${idx}`,
      label: a.label || '',
      dataUrl: String(map?.[a.id]?.dataUrl || '').trim() || a.dataUrl || '',
    }));
  }, [igLive?.highlights, packPreviewAssets]);

  const igLiveGridItems = useMemo(() => {
    const grid = Array.isArray(igLive?.grid) ? igLive.grid : [];
    const seed = [];
    const logo = activeBrand?.logoDataUrlSmall || activeBrand?.logoDataUrl || '';
    if (logo) seed.push(logo);
    for (const a of (packPreviewAssets || [])) {
      if (a.kind === 'highlight' && a.dataUrl) seed.push(a.dataUrl);
      if (seed.length >= 9) break;
    }
    const out = [];
    for (let i = 0; i < 9; i++) out.push(String(grid[i] || '').trim() || seed[i] || '');
    return out;
  }, [activeBrand?.logoDataUrl, activeBrand?.logoDataUrlSmall, igLive?.grid, packPreviewAssets]);

  const igLiveProfileName = useMemo(() => String(igLive?.profileName || '').trim(), [igLive?.profileName]);
  const igLiveBio = useMemo(() => String(igLive?.bio || '').trim(), [igLive?.bio]);
  const igLiveCaptions = useMemo(() => {
    const caps = Array.isArray(igLive?.captions) ? igLive.captions : [];
    const out = [];
    for (let i = 0; i < 9; i++) out.push(String(caps[i] || '').trim());
    return out;
  }, [igLive?.captions]);

  async function onIgPickFile(e) {
    const file = e?.target?.files?.[0] || null;
    try {
      e.target.value = '';
    } catch {
      // ignore
    }
    if (!file || !igPickTarget) return;

    const dataUrl = await fileToDataUrl(file);
    if (!dataUrl) return;

    const next = { ...(igLive || {}) };
    if (igPickTarget.kind === 'avatar') {
      next.avatarDataUrl = dataUrl;
      saveIgLive(next);
      return;
    }

    if (igPickTarget.kind === 'grid' && Number.isFinite(Number(igPickTarget.index))) {
      const idx = Number(igPickTarget.index);
      const grid = Array.isArray(next.grid) ? [...next.grid] : [];
      grid[idx] = dataUrl;
      next.grid = grid;
      saveIgLive(next);
      return;
    }

    if (igPickTarget.kind === 'highlight' && igPickTarget.id) {
      const map = next.highlights && typeof next.highlights === 'object' ? { ...next.highlights } : {};
      map[igPickTarget.id] = { dataUrl };
      next.highlights = map;
      saveIgLive(next);
    }
  }

  function ensureCanUseAi() {
    // Allow generation even without proxy; some providers work without a key.
    // If generation fails, the user can add a key or enable proxy.
    return true;
  }

  function promptForIgTarget(target) {
    const name = activeBrand?.name || 'brand';
    const industry = activeBrand?.industry || '';
    const tone = activeBrand?.tone || 'professional';
    const palList = (palette || []).filter(Boolean).slice(0, 8);
    const pal = palList.join(', ');
    const logoHint = hasLogo ? 'Use the brand logo as style inspiration (shapes/line weight), but do not include any text.' : '';
    const roleBg = roles?.background ? `Background: ${roles.background}` : '';
    const roleSurface = roles?.surface ? `Surface: ${roles.surface}` : '';
    const rolePrimary = roles?.primary ? `Primary: ${roles.primary}` : '';
    const roleAccent = roles?.accent ? `Accent: ${roles.accent}` : '';
    const roleText = roles?.text ? `Text: ${roles.text}` : '';
    const roleTextOnPrimary = roles?.textOnPrimary ? `Text on primary: ${roles.textOnPrimary}` : '';
    const roleBlock = [roleBg, roleSurface, rolePrimary, roleAccent, roleText, roleTextOnPrimary].filter(Boolean).join(' | ');

    const brandStyle = [
      `Brand: "${name}"`,
      industry ? `Industry: "${industry}"` : '',
      tone ? `Tone: "${tone}"` : '',
      pal ? `Palette (use ONLY these colors): ${pal}` : '',
      roleBlock ? `Theme roles: ${roleBlock}` : '',
      roleBg || rolePrimary || roleAccent
        ? `Color rules: Use background from "${roles?.background || roles?.surface || palList[0] || 'palette'}". Use primary for main subject ("${roles?.primary || palList[1] || 'palette'}"). Use accent sparingly (<=10%) ("${roles?.accent || palList[2] || 'palette'}").`
        : 'Color rules: Use max 3 colors per image from the palette. No extra colors.',
      logoHint,
      'Style: modern, clean, professional, vector-like, crisp edges.',
      'Rules: no text, no letters, no words, no watermark.',
      'Avoid: photorealism, busy backgrounds, random extra colors.',
      'If shading is used, keep it subtle and within palette tints only.',
    ]
      .filter(Boolean)
      .join('\n');

    if (target?.kind === 'avatar') {
      return `${brandStyle}\n\nCreate a clean Instagram profile avatar. Centered subject, high contrast, simple shapes. Transparent background. Not flat, but minimal (subtle shading allowed).`;
    }

    if (target?.kind === 'highlight') {
      const id = target?.id || '';
      const h = igLiveHighlights.find((x) => x.id === id);
      const label = h?.label || 'Highlight';
      const bgPref = roles?.surface || roles?.background || (palList[0] || '');
      return `${brandStyle}\n\nCreate an Instagram highlight cover representing: "${label}". Square 1:1. Use a solid background (prefer ${bgPref || 'a dark/neutral brand color'}). Centered icon, simple shapes, high contrast. Mini-illustration (not photorealistic).`;
    }

    if (target?.kind === 'grid') {
      const topic = String(target?.topic || '').trim();
      return `${brandStyle}\n\nCreate an Instagram square post image (1:1) that feels like this brand.${
        topic ? ` Theme: "${topic}".` : ''
      } No text. Clean composition, strong subject, subtle depth. Use the palette strictly.`;
    }

    return `${brandStyle}\n\nCreate an Instagram-style image. No text.`;
  }

  function topicsListFromText(raw) {
    return String(raw || '')
      .split('\n')
      .map((s) => String(s || '').trim())
      .filter(Boolean);
  }

  async function generatePostsBulk() {
    if (!activeBrand) return;
    if (!ensureCanUseAi()) return;
    const total = Math.max(1, Math.min(24, Number(postCount) || 9));
    const topics = topicsListFromText(postTopicsText);

    setIsGeneratingPosts(true);
    setPostsOp({ status: 'generating', message: '', index: 0, total });
    try {
      for (let i = 0; i < total; i++) {
        const topic = topics[i] || `Post ${i + 1}`;
        setPostsOp({ status: 'generating', message: topic, index: i + 1, total });

        const url = await onIgGenerateAi({ kind: 'grid', index: i, topic });
        setPostsOp({ status: 'loading', message: topic, index: i + 1, total });
        const pre = await preloadImage(url);
        if (!pre.ok && useProxy) {
          const url2 = await onIgGenerateAi({ kind: 'grid', index: i, topic }, { useProxyOverride: false });
          await preloadImage(url2);
        }
      }

      setPostsOp({ status: 'done', message: '', index: total, total });
    } catch (e) {
      setPostsOp({ status: 'error', message: String(e), index: 0, total });
    } finally {
      setIsGeneratingPosts(false);
      window.setTimeout(() => setPostsOp((p) => (p?.status === 'error' ? p : { status: 'idle', message: '', index: 0, total: 0 })), 2500);
    }
  }

  function promptForIgPreset(target, presetKey) {
    const k = String(presetKey || '').trim() || 'default';
    const base = promptForIgTarget(target);
    if (k === 'minimal') return `${base}\n\nExtra: Ultra-minimal, flat icon feel, no shading, very simple geometry.`;
    if (k === 'illustration') return `${base}\n\nExtra: Mini-illustration style, subtle shading, still clean and vector-like.`;
    if (k === 'luxury') return `${base}\n\nExtra: Luxury premium feel, elegant shapes, restrained accents, clean negative space.`;
    if (k === 'playful') return `${base}\n\nExtra: Playful but still on-brand, friendly shapes, slightly rounded, high contrast.`;
    return base;
  }

  function effectiveIgPrompt(target) {
    if (igEditPromptMode === 'custom') {
      const c = String(igEditPrompt || '').trim();
      if (c) return c;
    }
    return promptForIgPreset(target, igEditPreset);
  }

  async function preloadImage(url, { timeoutMs = 30000 } = {}) {
    const u = String(url || '').trim();
    if (!u) return { ok: false, error: 'missing_url' };
    return await new Promise((resolve) => {
      const img = new Image();
      let done = false;
      const t = window.setTimeout(() => {
        if (done) return;
        done = true;
        resolve({ ok: false, error: 'timeout' });
      }, timeoutMs);
      img.onload = () => {
        if (done) return;
        done = true;
        window.clearTimeout(t);
        resolve({ ok: true });
      };
      img.onerror = () => {
        if (done) return;
        done = true;
        window.clearTimeout(t);
        resolve({ ok: false, error: 'load_failed' });
      };
      img.src = u;
    });
  }

  function sanitizeReferenceImageForQuery(raw) {
    const s = String(raw || '').trim();
    if (!s) return '';
    // Avoid massive query strings / 414 errors.
    if (s.startsWith('data:')) return '';
    if (s.length > 1800) return '';
    if (!/^https?:\/\//i.test(s)) return '';
    return s;
  }

  async function onIgGenerateAi(target, { useProxyOverride } = {}) {
    if (!activeBrand) return;
    if (!ensureCanUseAi()) return;
    if (!target) return;

    const useProxyNow = useProxyOverride === undefined ? Boolean(useProxy) : Boolean(useProxyOverride);
    const prompt = effectiveIgPrompt(target);
    const isGrid = target?.kind === 'grid';
    const size = isGrid ? 1080 : 1024;
    const transparent = target?.kind === 'avatar';
    const apiKeyTrimmed = String(apiKey || '').trim() || undefined;
    const referenceImage = sanitizeReferenceImageForQuery(activeBrand?.logoDataUrlSmall || activeBrand?.logoDataUrl || '');
    const url = buildPollinationsImageUrl({
      prompt,
      model: 'flux',
      width: size,
      height: size,
      seed: undefined,
      enhance: false,
      negative_prompt:
        'text, watermark, letters, words, blurry, low quality, photo, photorealistic, busy background, frame, border, neon, random colors, rainbow, extra colors, off-brand colors, different palette, gradient background',
      safe: true,
      quality: 'high',
      transparent: transparent,
      referenceImage,
      apiKey: useProxyNow ? undefined : apiKeyTrimmed,
      useProxy: useProxyNow,
    });

    const next = { ...(igLive || {}) };
    const key = keyForIgTarget(target);
    if (key) {
      const promptsMeta = next.promptsMeta && typeof next.promptsMeta === 'object' ? { ...next.promptsMeta } : {};
      promptsMeta[key] = {
        mode: igEditPromptMode,
        preset: igEditPreset,
        customPrompt: igEditPromptMode === 'custom' ? String(igEditPrompt || '') : '',
        lastPrompt: prompt,
        updatedAt: new Date().toISOString(),
      };
      next.promptsMeta = promptsMeta;
    }
    if (target.kind === 'avatar') {
      next.avatarDataUrl = url;
      saveIgLive(next);
      return url;
    }
    if (target.kind === 'grid' && Number.isFinite(Number(target.index))) {
      const idx = Number(target.index);
      const grid = Array.isArray(next.grid) ? [...next.grid] : [];
      grid[idx] = url;
      next.grid = grid;
      saveIgLive(next);
      return url;
    }
    if (target.kind === 'highlight' && target.id) {
      const map = next.highlights && typeof next.highlights === 'object' ? { ...next.highlights } : {};
      map[target.id] = { dataUrl: url };
      next.highlights = map;
      saveIgLive(next);
      return url;
    }
    return url;
  }

  function currentUrlForIgTarget(target) {
    if (!target) return '';
    if (target.kind === 'avatar') return String(igLiveAvatarDataUrl || '').trim();
    if (target.kind === 'grid' && Number.isFinite(Number(target.index))) {
      const idx = Number(target.index);
      return String(igLiveGridItems?.[idx] || '').trim();
    }
    if (target.kind === 'highlight' && target.id) {
      const h = igLiveHighlights.find((x) => x.id === target.id);
      return String(h?.dataUrl || '').trim();
    }
    return '';
  }

  function currentTextForIgTarget(target) {
    if (!target) return '';
    if (target.kind === 'profileName') return String(igLiveProfileName || activeBrand?.name || '').trim();
    if (target.kind === 'bio') return String(igLiveBio || '').trim();
    if (target.kind === 'caption' && Number.isFinite(Number(target.index))) {
      const idx = Number(target.index);
      return String(igLiveCaptions?.[idx] || '').trim();
    }
    return '';
  }

  function renderIgEditor() {
    if (!igEditTarget) return null;
    const isTextTarget =
      igEditTarget.kind === 'profileName' || igEditTarget.kind === 'bio' || igEditTarget.kind === 'caption';
    const u = isTextTarget ? '' : currentUrlForIgTarget(igEditTarget);
    const tv = isTextTarget ? currentTextForIgTarget(igEditTarget) : '';
    const isGrid = igEditTarget.kind === 'grid' && Number.isFinite(Number(igEditTarget.index));
    const gridIdx = isGrid ? Number(igEditTarget.index) : -1;
    const gridCaption = isGrid ? String(igLiveCaptions?.[gridIdx] || '').trim() : '';
    return (
      <div className="BKIgEditor">
        <div className="BKIgEditorHeader">
          <div>
            <div className="BKIgEditorTitle">{isTextTarget ? t('brandKit.pack.liveEditTextTitle') : t('brandKit.pack.liveEditTitle')}</div>
            <div className="InlineHint" style={{ marginTop: 6 }}>
              {igEditTarget?.kind === 'avatar'
                ? t('brandKit.pack.livePickAvatar')
                : igEditTarget?.kind === 'profileName'
                  ? t('brandKit.pack.livePickProfileName')
                  : igEditTarget?.kind === 'bio'
                    ? t('brandKit.pack.livePickBio')
                    : igEditTarget?.kind === 'highlight'
                      ? t('brandKit.pack.livePickHighlight', { label: igLiveHighlights.find((x) => x.id === igEditTarget?.id)?.label || '' })
                      : igEditTarget?.kind === 'grid'
                        ? t('brandKit.pack.livePickGrid', { index: Number(igEditTarget?.index || 0) + 1 })
                        : igEditTarget?.kind === 'caption'
                          ? t('brandKit.pack.livePickCaption', { index: Number(igEditTarget?.index || 0) + 1 })
                          : ''}
            </div>
          </div>
          <button className="IconBtn" type="button" onClick={closeIgEdit} aria-label={t('common.close')}>
            ✕
          </button>
        </div>

        <div className="BKIgEditorBody">
          {!isTextTarget && u ? (
            <button className="BKEditPreview BKEditPreviewBtn" type="button" onClick={() => window.open(u, '_blank', 'noopener,noreferrer')}>
              <img className="BKEditPreviewImg" src={u} alt="" />
            </button>
          ) : null}

          {isTextTarget ? (
            <div className="Field" style={{ marginBottom: 12 }}>
              <div className="Field-label">{t('brandKit.pack.liveTextValue')}</div>
              {igEditTarget.kind === 'profileName' ? (
                <input
                  type="text"
                  value={tv}
                  onChange={(e) => {
                    const next = { ...(igLive || {}) };
                    next.profileName = e.target.value;
                    saveIgLive(next);
                  }}
                />
              ) : (
                <textarea
                  rows={4}
                  value={tv}
                  onChange={(e) => {
                    const next = { ...(igLive || {}) };
                    if (igEditTarget.kind === 'bio') {
                      next.bio = e.target.value;
                      saveIgLive(next);
                      return;
                    }
                    if (igEditTarget.kind === 'caption' && Number.isFinite(Number(igEditTarget.index))) {
                      const idx = Number(igEditTarget.index);
                      const caps = Array.isArray(next.captions) ? [...next.captions] : [];
                      caps[idx] = e.target.value;
                      next.captions = caps;
                      saveIgLive(next);
                    }
                  }}
                  style={{ resize: 'vertical' }}
                />
              )}
              {igEditTarget.kind !== 'profileName' ? <AIRewrite value={tv} field={igEditTarget.kind === 'bio' ? 'bio' : 'caption'} onApply={(value) => {
                const next = { ...(igLive || {}) };
                if (igEditTarget.kind === 'bio') next.bio = value;
                else {
                  const caps = Array.isArray(next.captions) ? [...next.captions] : [];
                  caps[Number(igEditTarget.index)] = value;
                  next.captions = caps;
                }
                saveIgLive(next);
              }} /> : null}
            </div>
          ) : null}

          <div className="Field" style={{ marginBottom: 10 }}>
            <div className="Field-label">{t('brandKit.pack.livePromptLabel')}</div>
            {igEditPromptMode !== 'custom' ? (
              <div className="Row Row--tight" style={{ alignItems: 'center' }}>
                <select value={igEditPreset} onChange={(e) => setIgEditPreset(e.target.value)} style={{ flex: 1 }}>
                  <option value="default">{t('brandKit.pack.livePreset.default')}</option>
                  <option value="minimal">{t('brandKit.pack.livePreset.minimal')}</option>
                  <option value="illustration">{t('brandKit.pack.livePreset.illustration')}</option>
                  <option value="luxury">{t('brandKit.pack.livePreset.luxury')}</option>
                  <option value="playful">{t('brandKit.pack.livePreset.playful')}</option>
                </select>
                <button className="Btn Btn--ghost" type="button" onClick={() => setIgEditPromptMode('custom')}>
                  {t('brandKit.pack.livePromptCustom')}
                </button>
              </div>
            ) : (
              <>
                <textarea
                  rows={4}
                  value={igEditPrompt}
                  placeholder={t('brandKit.pack.livePromptPlaceholder')}
                  onChange={(e) => setIgEditPrompt(e.target.value)}
                  style={{ resize: 'vertical' }}
                />
                <AIRewrite value={igEditPrompt} onApply={setIgEditPrompt} field="visual" mediaType="image" />
                <div className="Row Row--tight" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
                  <button className="Btn Btn--ghost" type="button" onClick={() => setIgEditPromptMode('preset')}>
                    {t('brandKit.pack.livePromptUsePresets')}
                  </button>
                </div>
              </>
            )}
            <div className="InlineHint" style={{ marginTop: 8 }}>
              {t('brandKit.pack.livePromptHint')}
            </div>
          </div>

          <div className="Row Row--tight" style={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {!isTextTarget ? (
              <button
                className="Btn Btn--ghost"
                type="button"
                onClick={() => {
                  if (!u) return;
                  window.open(u, '_blank', 'noopener,noreferrer');
                }}
                disabled={!u}
              >
                {t('common.open')}
              </button>
            ) : (
              <button
                className="Btn Btn--ghost"
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(String(tv || ''));
                  } catch {
                    // ignore
                  }
                }}
              >
                {t('common.copyText')}
              </button>
            )}

            <button
              className="Btn Btn--ghost"
              type="button"
              onClick={() => {
                setIgEditPromptMode('preset');
                setIgEditPreset('default');
                setIgEditPrompt('');
              }}
            >
              {t('brandKit.pack.livePromptReset')}
            </button>

            {!isTextTarget ? (
              <button className="Btn Btn--ghost" type="button" onClick={() => startIgUpload(igEditTarget)}>
                {t('brandKit.pack.liveUpload')}
              </button>
            ) : null}

            {!isTextTarget ? (
              <button
                className="Btn"
                type="button"
                onClick={async () => {
                  if (!igEditTarget) return;
                  setIgAiBusy(true);
                  setIgOp({ status: 'generating', message: '' });
                  try {
                    const url2 = await onIgGenerateAi(igEditTarget);
                    if (!url2) {
                      setIgOp({ status: 'error', message: t('common.error') });
                      return;
                    }
                    setIgOp({ status: 'loading', message: '' });
                    const pre = await preloadImage(url2);
                    if (!pre.ok) {
                      // Common in local dev if proxy is enabled but /api/* isn't running.
                      if (useProxy) {
                        setIgOp({ status: 'loading', message: 'Proxy not available — trying direct…' });
                        const url3 = await onIgGenerateAi(igEditTarget, { useProxyOverride: false });
                        const pre2 = await preloadImage(url3);
                        if (pre2.ok) {
                          setIgOp({ status: 'done', message: '' });
                          return;
                        }
                      }
                      setIgOp({ status: 'error', message: t('preview.loadFailedTitleImage') });
                      return;
                    }
                    setIgOp({ status: 'done', message: '' });
                  } catch (e) {
                    setIgOp({ status: 'error', message: String(e) });
                  } finally {
                    setIgAiBusy(false);
                  }
                }}
                disabled={igAiBusy}
              >
                {igAiBusy ? t('common.generating') : t('brandKit.pack.liveGenerateAi')}
              </button>
            ) : null}
          </div>

          {!isTextTarget ? (
            <div className="InlineHint" style={{ marginTop: 10 }}>
              {hasLogo
                ? 'Brand-aware AI: matches your palette and theme roles for consistent results.'
                : 'Tip: upload a logo and set Primary/Accent for more consistent AI images.'}
            </div>
          ) : null}

          {isGrid ? (
            <div className="Field" style={{ marginTop: 14 }}>
              <div className="Field-label">{t('brandKit.pack.liveCaption')}</div>
              <textarea
                rows={3}
                value={gridCaption}
                placeholder={t('brandKit.pack.liveCaptionPlaceholder')}
                onChange={(e) => {
                  const next = { ...(igLive || {}) };
                  const caps = Array.isArray(next.captions) ? [...next.captions] : [];
                  caps[gridIdx] = e.target.value;
                  next.captions = caps;
                  saveIgLive(next);
                }}
                style={{ resize: 'vertical' }}
              />
              <AIRewrite value={gridCaption} field="caption" onApply={(value) => {
                const next = { ...(igLive || {}) };
                const caps = Array.isArray(next.captions) ? [...next.captions] : [];
                caps[gridIdx] = value;
                next.captions = caps;
                saveIgLive(next);
              }} />
            </div>
          ) : null}

          {igOp.status !== 'idle' ? (
            <div className="InlineHint" style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
              {igOp.status === 'generating' || igOp.status === 'loading' ? <div className="Spinner" /> : null}
              <div>
                {igOp.status === 'generating'
                  ? t('common.generating')
                  : igOp.status === 'loading'
                    ? t('common.loading')
                    : igOp.status === 'done'
                      ? t('brandKit.pack.liveDone')
                      : t('common.error')}
                {igOp.message ? ` — ${igOp.message}` : ''}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  function labelForIgEditTarget(target) {
    if (!target) return '';
    if (target.kind === 'avatar') return t('brandKit.pack.livePickAvatar');
    if (target.kind === 'profileName') return t('brandKit.pack.livePickProfileName');
    if (target.kind === 'bio') return t('brandKit.pack.livePickBio');
    if (target.kind === 'highlight') {
      const lbl = igLiveHighlights.find((x) => x.id === target.id)?.label || '';
      return t('brandKit.pack.livePickHighlight', { label: lbl });
    }
    if (target.kind === 'grid') return t('brandKit.pack.livePickGrid', { index: Number(target.index || 0) + 1 });
    if (target.kind === 'caption') return t('brandKit.pack.livePickCaption', { index: Number(target.index || 0) + 1 });
    return '';
  }

  async function fileToDataUrl(file) {
    const f = file || null;
    if (!f) return '';
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || ''));
      r.onerror = () => resolve('');
      r.readAsDataURL(f);
    });
  }

  async function onUploadIconForLabel(rawLabel, file) {
    if (!activeBrand) return;
    const { key } = parseBilingualLabel(rawLabel);
    if (!key) return;
    const dataUrl = await fileToDataUrl(file);
    if (!dataUrl) return;

    const lib = (activeBrand.highlightIcons && typeof activeBrand.highlightIcons === 'object') ? activeBrand.highlightIcons : {};
    const nextLib = { ...lib, [iconCacheKey(key)]: { dataUrl, prompt: 'manual', updatedAt: new Date().toISOString() } };
    onUpdateActiveBrand?.({ highlightIcons: nextLib });
  }

  function onClearIconForLabel(rawLabel) {
    if (!activeBrand) return;
    const { key } = parseBilingualLabel(rawLabel);
    if (!key) return;
    const lib = (activeBrand.highlightIcons && typeof activeBrand.highlightIcons === 'object') ? activeBrand.highlightIcons : {};
    const k = iconCacheKey(key);
    if (!lib?.[k]) return;
    const nextLib = { ...lib };
    delete nextLib[k];
    onUpdateActiveBrand?.({ highlightIcons: nextLib });
  }

  async function onGenerateIconForLabel(rawLabel) {
    const pair = parseBilingualLabel(rawLabel);
    if (!pair.key) return;
    await getOrGenerateIcon(pair);
  }

  async function buildPack() {
    if (!activeBrand) return;
    setIsBuildingPack(true);
    try {
      const nextAssets = [];

      if (includeAvatar) {
        const c = await renderProfileAvatar({
          size: 1024,
          palette,
          colorSystem,
          colorMode,
          logoDataUrl: activeBrand.logoDataUrlSmall || activeBrand.logoDataUrl,
          brandName: activeBrand.name,
          fontFamily: brandFontFamily,
        });
        const dataUrl = await canvasToPngDataUrl(c);
        nextAssets.push({
          id: `avatar-${Date.now()}`,
          kind: 'avatar',
          label: t('brandKit.assets.avatar'),
          dataUrl,
          filename: 'brand-avatar.png',
        });
      }

      if (includeHighlights) {
        const backdrop = computedBackdrop();
        const labels = highlightLabels.map((s) => String(s || '').trim()).filter(Boolean).slice(0, 24);
        for (let i = 0; i < labels.length; i++) {
          const pair = parseBilingualLabel(labels[i]);
          const icon = await getOrGenerateIcon(pair);

          const targets = [];
          if (generateEnglish && pair.en) targets.push({ lang: 'en', text: pair.en });
          if (generateArabic && pair.ar) targets.push({ lang: 'ar', text: pair.ar });
          if (!targets.length) targets.push({ lang: 'en', text: pair.en || pair.ar || labels[i] });

          for (let j = 0; j < targets.length; j++) {
            const labelText = targets[j].text;
            const c = await renderHighlightCover({
              size: 1024,
              palette,
              colorSystem,
              colorMode,
              label: labelText,
              iconDataUrl: icon?.dataUrl || '',
              iconBackdrop: backdrop,
              fontFamily: brandFontFamily,
            });
            const dataUrl = await canvasToPngDataUrl(c);
            nextAssets.push({
              id: `highlight-${i}-${j}-${Date.now()}`,
              kind: 'highlight',
              label: labelText,
              dataUrl,
              filename: `highlight-${targets[j].lang}-${i + 1}-${labelText.replace(/\s+/g, '-').toLowerCase()}.png`,
            });
          }
        }
      }

      if (includePosts) {
        const total = Math.max(1, Math.min(24, Number(postCount) || 9));
        const grid = Array.isArray(igLive?.grid) ? igLive.grid : [];
        const caps = Array.isArray(igLive?.captions) ? igLive.captions : [];

        for (let i = 0; i < total; i++) {
          const url = String(grid[i] || '').trim();
          if (url) {
            nextAssets.push({
              id: `post-${i}-${Date.now()}`,
              kind: 'post',
              label: t('brandKit.assets.post', { index: i + 1 }),
              dataUrl: url,
              filename: `post-${i + 1}.png`,
            });
          }

          const cap = String(caps[i] || '').trim();
          if (cap) {
            nextAssets.push({
              id: `caption-${i}-${Date.now()}`,
              kind: 'caption',
              label: t('brandKit.assets.caption', { index: i + 1 }),
              dataUrl: `data:text/plain;charset=utf-8,${encodeURIComponent(cap)}`,
              filename: `post-${i + 1}-caption.txt`,
            });
          }
        }
      }

      setAssets(nextAssets);
      setStep(3);
    } finally {
      setIsBuildingPack(false);
    }
  }

  function downloadAsset(a) {
    downloadDataUrl(a?.dataUrl, a?.filename || 'asset.png');
  }

  function downloadAllAssets() {
    (assets || []).forEach((a) => downloadAsset(a));
  }

  return (
    <div className="BrandKit">
      {error ? (
        <div className="Toast" role="status">
          {error}
        </div>
      ) : null}

      {/* IG edit UI is inline (no modal dialogs). */}

      <div className="BrandKitHeader">
        <div>
          <div className="BrandKitTitle">{t('brandKit.title')}</div>
          <div className="BrandKitSubtitle">{t('brandKit.subtitle')}</div>
        </div>
      </div>

      <div className="BrandKitSteps" role="tablist" aria-label={t('brandKit.stepsLabel')}>
        {steps.map((s) => (
          <StepPillButton
            key={s.id}
            active={step === s.id}
            done={step > s.id}
            label={s.label}
            index={s.id}
            onClick={() => setStep(s.id)}
          />
        ))}
      </div>

      {step === 1 ? (
        <div className="Grid">
          <AccordionCard
            id="bk_basics"
            title={t('brandKit.basics.title')}
            defaultOpen={true}
            openState={openSections}
            onToggle={toggleSection}
          >
              <div className="Row Row--tight" style={{ marginBottom: 10 }}>
                <label className="Field" style={{ flex: 1 }} htmlFor="brandKitBrandSelect">
                  <div className="Field-label">{t('brand.profile')}</div>
                  <select
                    id="brandKitBrandSelect"
                    value={activeBrandId || ''}
                    onChange={(e) => onSetActiveBrandId(e.target.value)}
                  >
                    {brands.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name || t('brand.untitled')}
                      </option>
                    ))}
                  </select>
                </label>
                <button className="Btn Btn--ghost" type="button" onClick={onCreateBrand}>
                  {t('brand.new')}
                </button>
              </div>

              <div className="Grid">
                <label className="Field">
                  <div className="Field-label">{t('brand.name')}</div>
                  <input
                    type="text"
                    value={activeBrand?.name || ''}
                    onChange={(e) => onUpdateActiveBrand({ name: e.target.value })}
                  />
                </label>
                <label className="Field">
                  <div className="Field-label">{t('brand.industry')}</div>
                  <input
                    type="text"
                    value={activeBrand?.industry || ''}
                    placeholder={t('brand.industryPlaceholder')}
                    onChange={(e) => onUpdateActiveBrand({ industry: e.target.value })}
                  />
                </label>
                <label className="Field">
                  <div className="Field-label">{t('brand.tone')}</div>
                  <select value={activeBrand?.tone || 'professional'} onChange={(e) => onUpdateActiveBrand({ tone: e.target.value })}>
                    <option value="professional">{t('brand.toneOptions.professional')}</option>
                    <option value="luxury">{t('brand.toneOptions.luxury')}</option>
                    <option value="playful">{t('brand.toneOptions.playful')}</option>
                    <option value="friendly">{t('brand.toneOptions.friendly')}</option>
                    <option value="bold">{t('brand.toneOptions.bold')}</option>
                  </select>
                </label>
              </div>
          </AccordionCard>

          <AccordionCard
            id="bk_logo"
            title={t('brandKit.logo.title')}
            defaultOpen={true}
            openState={openSections}
            onToggle={toggleSection}
          >
            <div className="Row Row--tight" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
              <button
                className="Btn Btn--ghost"
                type="button"
                onClick={onGenerateLogo}
                disabled={isGeneratingLogo || (!useProxy && !String(apiKey || '').trim()) || !activeBrand}
              >
                {isGeneratingLogo ? t('brandKit.logo.generating') : t('brandKit.logo.generate')}
              </button>
            </div>
            <ImageDrop
              title={t('brand.logoDropTitle')}
              hint={t('brand.logoDropHint')}
              uploadLabel={t('common.upload')}
              clearLabel={t('common.clear')}
                valueDataUrl={activeBrand?.logoDataUrlSmall || activeBrand?.logoDataUrl || ''}
                valueName={activeBrand?.logoName || ''}
                onFile={onUploadLogo}
                onClear={onClearLogo}
                disabled={!activeBrand}
              />

              <div className="InlineHint" style={{ marginTop: 10 }}>
                {hasLogo ? t('brandKit.logo.ready') : t('brandKit.logo.missing')}
              </div>
          </AccordionCard>
        </div>
      ) : null}

      {step === 2 ? (
        <>
          <div className="BKStudioLayout" style={{ marginTop: 12 }}>
            <input
              ref={igPickInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={onIgPickFile}
            />

            <div className="BKStudioMain">
              <div className="Card BKPreviewCard">
                <div className="Card-title">{t('brandKit.pack.livePreviewTitle')}</div>
                <div className="Card-body">
                  <div className="Row Row--tight" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className="InlineHint" style={{ margin: 0 }}>
                      {t('brandKit.pack.liveHint')}
                    </div>
                    {isPreviewingPack ? <div className="Badge">{t('common.generating')}</div> : null}
                    <button className="Btn Btn--ghost" type="button" onClick={() => saveIgLive(null)} disabled={!activeBrand?.brandKitIgLivePreview}>
                      {t('brandKit.pack.liveReset')}
                    </button>
                  </div>
                  <div style={{ marginTop: 10, display: 'grid', placeItems: 'center' }}>
                    <InstagramBrandKitLivePreview
                      brandName={activeBrand?.name || ''}
                      profileName={igLiveProfileName}
                      bio={igLiveBio}
                      avatarDataUrl={igLiveAvatarDataUrl}
                      highlights={igLiveHighlights}
                      gridItems={igLiveGridItems}
                      size={mobile ? 'md' : 'xl'}
                      onPickAvatar={() => openIgEdit({ kind: 'avatar' })}
                      onPickProfileName={() => openIgEdit({ kind: 'profileName' })}
                      onPickBio={() => openIgEdit({ kind: 'bio' })}
                      onPickHighlight={(idx) => openIgEdit({ kind: 'highlight', id: igLiveHighlights?.[idx]?.id })}
                      onPickGrid={(idx) => openIgEdit({ kind: 'grid', index: idx })}
                    />
                  </div>
                </div>
              </div>

              <details className="Card BKBrandKitSettings" open style={{ marginTop: 12 }}>
                <summary className="Card-title Card-title--summary">{t('brandKit.pack.brandSettingsTitle')}</summary>
                <div className="Card-body">
                  <div className="SettingsTabs" role="tablist" aria-label={t('brandKit.pack.brandSettingsTitle')} style={{ marginBottom: mobile ? 8 : 10 }}>
                    <button
                      type="button"
                      role="tab"
                      className={`SettingsTab ${bkSettingsTab === 'identity' ? 'SettingsTab--active' : ''}`}
                      onClick={() => setBkSettingsTab('identity')}
                      aria-selected={bkSettingsTab === 'identity' ? 'true' : 'false'}
                      tabIndex={bkSettingsTab === 'identity' ? 0 : -1}
                    >
                      {t('brandKit.settingsTabs.identity')}
                    </button>
                    <button
                      type="button"
                      role="tab"
                      className={`SettingsTab ${bkSettingsTab === 'pack' ? 'SettingsTab--active' : ''}`}
                      onClick={() => setBkSettingsTab('pack')}
                      aria-selected={bkSettingsTab === 'pack' ? 'true' : 'false'}
                      tabIndex={bkSettingsTab === 'pack' ? 0 : -1}
                    >
                      {t('brandKit.settingsTabs.pack')}
                    </button>
                  </div>
                  <div className="Grid">
                    {bkSettingsTab === 'identity' ? (
                      <>
                    <AccordionCard
            id="bk_identity_colors"
            title={t('brandKit.identity.colorsTitle')}
            defaultOpen={true}
            openState={openSections}
            onToggle={toggleSection}
          >
              <div className="Row Row--tight" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="Seg" role="tablist" aria-label={t('brandKit.identity.colorMode')}>
                  <button
                    type="button"
                    className={`SegBtn ${colorMode === 'dark' ? 'SegBtn--active' : ''}`}
                    onClick={() => setColorMode('dark')}
                  >
                    {t('brandKit.identity.modeDark')}
                  </button>
                  <button
                    type="button"
                    className={`SegBtn ${colorMode === 'light' ? 'SegBtn--active' : ''}`}
                    onClick={() => setColorMode('light')}
                  >
                    {t('brandKit.identity.modeLight')}
                  </button>
                </div>
                <button className="Btn Btn--ghost" type="button" onClick={regenColorSystemFromPalette}>
                  {t('brandKit.identity.regenFromPalette')}
                </button>
              </div>

              {palette.length ? (
                <div className="PaletteGrid">
                  {palette.map((hex, idx) => (
                    <div key={`${hex}-${idx}`} className="Swatch Swatch--editable" title={hex}>
                      <input
                        className="Swatch-color Swatch-color--input"
                        type="color"
                        value={hex}
                        onChange={(e) => updatePaletteColor(idx, e.target.value)}
                        aria-label={t('brandKit.identity.colorPicker')}
                      />
                      <input
                        className="SwatchHexInput"
                        type="text"
                        value={hex}
                        onChange={(e) => updatePaletteColor(idx, e.target.value)}
                        aria-label={t('brandKit.identity.colorHex')}
                      />
                      <button className="IconBtn IconBtn--sm" type="button" onClick={() => removePaletteColor(idx)} aria-label={t('common.remove')}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="Empty">{t('brand.noPalette')}</div>
              )}

              <div className="Row Row--tight" style={{ marginTop: 12, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div className="Row Row--tight" style={{ flexWrap: 'wrap' }}>
                  <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} aria-label={t('brandKit.identity.addColor')} />
                  <input
                    className="SwatchHexInput"
                    type="text"
                    value={newColor}
                    onChange={(e) => setNewColor(e.target.value)}
                    aria-label={t('brandKit.identity.addColor')}
                  />
                </div>
                <button className="Btn Btn--ghost" type="button" onClick={addPaletteColor}>
                  + {t('brandKit.identity.addColor')}
                </button>
              </div>

              <div className="InlineHint">{t('brandKit.identity.colorsHint')}</div>
          </AccordionCard>

          <AccordionCard
            id="bk_identity_fonts"
            title={t('brandKit.identity.fontsTitle')}
            defaultOpen={false}
            openState={openSections}
            onToggle={toggleSection}
          >
              <div className="Grid Grid--single">
                <label className="Field">
                  <div className="Field-label">{t('brandKit.identity.fontArabic')}</div>
                  <select
                    value={activeBrand?.fontArabic || 'ibm_plex_sans_arabic'}
                    onChange={(e) => onUpdateActiveBrand({ fontArabic: e.target.value })}
                  >
                    <option value="ibm_plex_sans_arabic">IBM Plex Sans Arabic</option>
                    <option value="noto_sans_arabic">Noto Sans Arabic</option>
                    <option value="noto_kufi_arabic">Noto Kufi Arabic</option>
                  </select>
                </label>
                <label className="Field">
                  <div className="Field-label">{t('brandKit.identity.fontLatin')}</div>
                  <select
                    value={activeBrand?.fontLatin || 'system'}
                    onChange={(e) => onUpdateActiveBrand({ fontLatin: e.target.value })}
                  >
                    <option value="system">System UI</option>
                    <option value="inter">Inter</option>
                    <option value="ibm_plex_sans">IBM Plex Sans</option>
                  </select>
                </label>
              </div>
              <div className="InlineHint">{t('brandKit.identity.fontsHint')}</div>
          </AccordionCard>

          <AccordionCard
            id="bk_identity_roles"
            title={t('brandKit.identity.rolesTitle')}
            defaultOpen={false}
            openState={openSections}
            onToggle={toggleSection}
          >
              {roles ? (
                <>
                  <div className="RoleGrid">
                    {[
                      { k: 'background', label: t('brandKit.identity.roleBackground') },
                      { k: 'surface', label: t('brandKit.identity.roleSurface') },
                      { k: 'primary', label: t('brandKit.identity.rolePrimary') },
                      { k: 'secondary', label: t('brandKit.identity.roleSecondary') },
                      { k: 'accent', label: t('brandKit.identity.roleAccent') },
                      { k: 'text', label: t('brandKit.identity.roleText') },
                      { k: 'textMuted', label: t('brandKit.identity.roleTextMuted') },
                      { k: 'border', label: t('brandKit.identity.roleBorder') },
                      { k: 'textOnPrimary', label: t('brandKit.identity.roleTextOnPrimary') },
                    ].map((r) => (
                      (() => {
                        const pv = rolePreviewForKey(r.k);
                        const ratio = pv.bg && pv.fg ? contrastRatio(pv.fg, pv.bg) : NaN;
                        const badge = badgeForContrast(ratio);
                        const showRatio = Number.isFinite(ratio) && (r.k === 'background' || r.k === 'surface' || r.k === 'primary' || r.k === 'textOnPrimary');

                        return (
                          <div key={r.k} className={`RoleRow ${locks?.[r.k] ? 'RoleRow--locked' : ''}`}>
                            <div className="RoleRowTop">
                              <div className="RoleLabel">{r.label}</div>
                              {showRatio ? (
                                <div className={`Badge ${badge.variant === 'ok' ? 'Badge--ok' : badge.variant === 'bad' ? 'Badge--bad' : 'Badge--warn'}`}>
                                  {badge.label} ({ratio.toFixed(2)})
                                </div>
                              ) : null}
                            </div>

                            <div className="RoleControls">
                              <div
                                className="RolePreview"
                                aria-hidden="true"
                                style={{
                                  background: pv.bg,
                                  color: pv.fg,
                                  borderColor: pv.border || 'rgba(255,255,255,0.10)',
                                }}
                              >
                                Aa
                              </div>

                              <input
                                className="RoleColor"
                                type="color"
                                value={roles[r.k]}
                                onChange={(e) => updateRole(r.k, e.target.value)}
                                aria-label={r.label}
                                disabled={Boolean(locks?.[r.k])}
                              />
                              <input
                                className="SwatchHexInput"
                                type="text"
                                value={roles[r.k]}
                                onChange={(e) => updateRole(r.k, e.target.value)}
                                aria-label={r.label}
                                disabled={Boolean(locks?.[r.k])}
                              />
                              <button className="Btn Btn--ghost RoleLock" type="button" onClick={() => toggleLock(r.k)}>
                                {locks?.[r.k] ? t('brandKit.identity.locked') : t('brandKit.identity.unlocked')}
                              </button>
                            </div>
                          </div>
                        );
                      })()
                    ))}
                  </div>

                  <div className="Row Row--tight" style={{ marginTop: 12, justifyContent: 'space-between' }}>
                    <div className="Row Row--tight">
                      <div className={`Badge ${badgeForContrast(contrastBg).variant === 'ok' ? 'Badge--ok' : 'Badge--warn'}`}>
                        {t('brandKit.identity.contrastBg')}: {badgeForContrast(contrastBg).label} ({contrastBg.toFixed(2)})
                      </div>
                      <div className={`Badge ${badgeForContrast(contrastSurface).variant === 'ok' ? 'Badge--ok' : 'Badge--warn'}`}>
                        {t('brandKit.identity.contrastSurface')}: {badgeForContrast(contrastSurface).label} ({contrastSurface.toFixed(2)})
                      </div>
                      <div className={`Badge ${badgeForContrast(contrastPrimary).variant === 'ok' ? 'Badge--ok' : 'Badge--warn'}`}>
                        {t('brandKit.identity.contrastPrimary')}: {badgeForContrast(contrastPrimary).label} ({contrastPrimary.toFixed(2)})
                      </div>
                    </div>
                  </div>

                </>
              ) : (
                <div className="InlineHint">{t('brandKit.identity.rolesMissing')}</div>
              )}
          </AccordionCard>
                      </>
                    ) : null}

                    {bkSettingsTab === 'pack' ? (
                      <>
          <AccordionCard
            id="bk_pack"
            title={t('brandKit.pack.title')}
            defaultOpen={true}
            openState={openSections}
            onToggle={toggleSection}
          >
            <div className="Grid Grid--single">
              <label className="Field Field--toggle">
                <input type="checkbox" checked={includeAvatar} onChange={(e) => setIncludeAvatar(e.target.checked)} />
                <span>{t('brandKit.pack.includeAvatar')}</span>
              </label>
              <label className="Field Field--toggle">
                <input type="checkbox" checked={includeHighlights} onChange={(e) => setIncludeHighlights(e.target.checked)} />
                <span>{t('brandKit.pack.includeHighlights')}</span>
              </label>
              <label className="Field Field--toggle">
                <input type="checkbox" checked={includePosts} onChange={(e) => setIncludePosts(e.target.checked)} />
                <span>{t('brandKit.pack.includePosts')}</span>
              </label>

              {includeHighlights ? (
                <div className="CardFlat">
                  <div className="Row Row--tight" style={{ justifyContent: 'space-between' }}>
                    <div className="Hint">{t('brandKit.pack.highlightLabels')}</div>
                    <button className="Btn Btn--ghost" type="button" onClick={addLabel}>
                      + {t('brandKit.pack.addLabel')}
                    </button>
                  </div>
                  <div className="Row Row--tight" style={{ marginTop: 10, justifyContent: 'space-between' }}>
                    <label className="Field Field--toggle" style={{ margin: 0 }}>
                      <input type="checkbox" checked={generateEnglish} onChange={(e) => setGenerateEnglish(e.target.checked)} />
                      <span>{t('brandKit.pack.langEnglish')}</span>
                    </label>
                    <label className="Field Field--toggle" style={{ margin: 0 }}>
                      <input type="checkbox" checked={generateArabic} onChange={(e) => setGenerateArabic(e.target.checked)} />
                      <span>{t('brandKit.pack.langArabic')}</span>
                    </label>
                    <label className="Field Field--toggle" style={{ margin: 0 }}>
                      <input type="checkbox" checked={regenerateIcons} onChange={(e) => setRegenerateIcons(e.target.checked)} />
                      <span>{t('brandKit.pack.regenerateIcons')}</span>
                    </label>
                  </div>

                  <div className="Row Row--tight" style={{ marginTop: 10, justifyContent: 'space-between', gap: 10 }}>
                    <div className="Hint">{t('brandKit.pack.artStyle')}</div>
                    <select value={highlightArtStyle} onChange={(e) => setHighlightArtStyle(e.target.value)}>
                      <option value="illustration">{t('brandKit.pack.artIllustration')}</option>
                      <option value="sub_logo">{t('brandKit.pack.artSubLogo')}</option>
                    </select>
                  </div>

                  <div className="Row Row--tight" style={{ marginTop: 10, justifyContent: 'space-between', gap: 10 }}>
                    <div className="Hint">{t('brandKit.pack.iconBackdrop')}</div>
                    <div className="Row Row--tight" style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <select
                        value={iconBackdropMode}
                        onChange={(e) => {
                          const mode = e.target.value;
                          setIconBackdropMode(mode);
                          saveIconBackdrop({ mode, color: iconBackdropColor, opacity: iconBackdropOpacity });
                        }}
                      >
                        <option value="transparent">{t('brandKit.pack.backdropTransparent')}</option>
                        <option value="surface">{t('brandKit.pack.backdropSurface')}</option>
                        <option value="primary">{t('brandKit.pack.backdropPrimary')}</option>
                        <option value="accent">{t('brandKit.pack.backdropAccent')}</option>
                        <option value="custom">{t('brandKit.pack.backdropCustom')}</option>
                      </select>
                      {iconBackdropMode === 'custom' ? (
                        <>
                          <input
                            type="color"
                            value={normalizeHex(iconBackdropColor) || '#000000'}
                            onChange={(e) => {
                              setIconBackdropColor(e.target.value);
                              saveIconBackdrop({ mode: iconBackdropMode, color: e.target.value, opacity: iconBackdropOpacity });
                            }}
                            aria-label={t('brandKit.pack.backdropCustom')}
                          />
                          <input
                            className="SwatchHexInput"
                            type="text"
                            value={iconBackdropColor}
                            onChange={(e) => {
                              setIconBackdropColor(e.target.value);
                              saveIconBackdrop({ mode: iconBackdropMode, color: e.target.value, opacity: iconBackdropOpacity });
                            }}
                            aria-label={t('brandKit.pack.backdropCustom')}
                          />
                        </>
                      ) : null}
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={5}
                        value={Math.round(clampNumber(iconBackdropOpacity, { min: 0, max: 1 }) * 100)}
                        onChange={(e) => {
                          const v = clampNumber(Number(e.target.value) / 100, { min: 0, max: 1 });
                          setIconBackdropOpacity(v);
                          saveIconBackdrop({ mode: iconBackdropMode, color: iconBackdropColor, opacity: v });
                        }}
                        style={{ width: 88 }}
                        aria-label={t('brandKit.pack.backdropOpacity')}
                        title={t('brandKit.pack.backdropOpacity')}
                      />
                    </div>
                  </div>
                  <div className="Grid Grid--single" style={{ marginTop: 10 }}>
                    {highlightLabels.map((v, idx) => (
                      <div key={idx} className="HighlightRow">
                        <div className="HighlightIconCell">
                          {(() => {
                            const pair = parseBilingualLabel(v);
                            const cached = pair.key ? getCachedIcon(iconCacheKey(pair.key)) : null;
                            const backdrop = computedBackdrop();
                            const bg =
                              backdrop.color && backdrop.opacity > 0
                                ? `color-mix(in srgb, ${backdrop.color} ${Math.round(backdrop.opacity * 100)}%, transparent)`
                                : '';
                            return cached?.dataUrl ? (
                              <>
                                <div className="HighlightIconBackdrop" style={bg ? { background: bg } : undefined} />
                                <img className="HighlightIconImg" src={cached.dataUrl} alt="" />
                              </>
                            ) : (
                              <div className="HighlightIconEmpty" aria-hidden="true">
                                ✦
                              </div>
                            );
                          })()}
                        </div>

                        <div className="HighlightMainCell">
                          <div className="Row Row--tight" style={{ justifyContent: 'space-between' }}>
                            <input
                              type="text"
                              value={v}
                              onChange={(e) => updateLabel(idx, e.target.value)}
                              placeholder={t('brandKit.pack.labelPlaceholder')}
                            />
                            <button className="IconBtn IconBtn--sm" type="button" onClick={() => removeLabel(idx)} aria-label={t('common.remove')}>
                              ✕
                            </button>
                          </div>

                          <div className="Row Row--tight" style={{ marginTop: 8 }}>
                            <button
                              className="Btn Btn--ghost"
                              type="button"
                              onClick={() => onGenerateIconForLabel(v)}
                              disabled={!useProxy && !String(apiKey || '').trim()}
                            >
                              {highlightArtStyle === 'sub_logo' ? t('brandKit.pack.generateSubLogo') : t('brandKit.pack.generateIcon')}
                            </button>

                            <label className="Btn Btn--ghost" style={{ cursor: 'pointer' }}>
                              {t('brandKit.pack.uploadIcon')}
                              <input
                                className="HiddenInput"
                                type="file"
                                accept="image/*"
                                onChange={(e) => onUploadIconForLabel(v, e.target.files?.[0] || null)}
                              />
                            </label>

                            <button className="Btn Btn--ghost" type="button" onClick={() => onClearIconForLabel(v)}>
                              {t('common.clear')}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="InlineHint">{t('brandKit.pack.bilingualHint')}</div>
                </div>
              ) : null}

              {includePosts ? (
                <div className="CardFlat">
                  <div className="Row Row--tight" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className="Hint">{t('brandKit.pack.postsTitle')}</div>
                    <button className="Btn Btn--ghost" type="button" onClick={generatePostsBulk} disabled={isGeneratingPosts}>
                      {isGeneratingPosts ? t('common.generating') : t('brandKit.pack.generatePostsAi')}
                    </button>
                  </div>

                  <div className="Row Row--tight" style={{ marginTop: 10, justifyContent: 'space-between', gap: 10 }}>
                    <div className="Hint">{t('brandKit.pack.postCount')}</div>
                    <select value={postCount} onChange={(e) => setPostCount(Number(e.target.value) || 9)}>
                      <option value={3}>3</option>
                      <option value={6}>6</option>
                      <option value={9}>9</option>
                      <option value={12}>12</option>
                    </select>
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <div className="Hint">{t('brandKit.pack.postTopics')}</div>
                    <textarea
                      rows={5}
                      value={postTopicsText}
                      onChange={(e) => setPostTopicsText(e.target.value)}
                      placeholder={t('brandKit.pack.postTopicsPlaceholder')}
                      style={{ resize: 'vertical' }}
                    />
                    <AIRewrite value={postTopicsText} onApply={setPostTopicsText} field="topics" />
                    <div className="InlineHint" style={{ marginTop: 8 }}>
                      {t('brandKit.pack.postTopicsHint')}
                    </div>
                  </div>

                  {postsOp.status !== 'idle' ? (
                    <div className="InlineHint" style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
                      {postsOp.status === 'generating' || postsOp.status === 'loading' ? <div className="Spinner" /> : null}
                      <div>
                        {postsOp.status === 'generating'
                          ? `${t('common.generating')} (${postsOp.index}/${postsOp.total})`
                          : postsOp.status === 'loading'
                            ? `${t('common.loading')} (${postsOp.index}/${postsOp.total})`
                            : postsOp.status === 'done'
                              ? t('brandKit.pack.liveDone')
                              : t('common.error')}
                        {postsOp.message ? ` — ${postsOp.message}` : ''}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="Row Row--tight" style={{ marginTop: 12 }}>
              <button
                className="Btn"
                type="button"
                onClick={buildPack}
                disabled={isBuildingPack || (!includeAvatar && !includeHighlights && !includePosts)}
              >
                {isBuildingPack ? t('common.generating') : t('brandKit.pack.generate')}
              </button>
              <button className="Btn Btn--ghost" type="button" onClick={() => setStep(3)} disabled={!assets.length}>
                {t('brandKit.pack.viewExport')}
              </button>
            </div>
            <div className="InlineHint">{t('brandKit.pack.hint')}</div>
          </AccordionCard>
                      </>
                    ) : null}
                  </div>
                </div>
              </details>
            </div>

            <aside className="BKStudioSide">
              <div className="Card">
                <div className="Card-title">{t('brandKit.pack.liveInspectorTitle')}</div>
                <div className="Card-body">
                  {igEditTarget ? renderIgEditor() : <div className="InlineHint">{t('brandKit.pack.liveInspectorEmpty')}</div>}
                </div>
              </div>
            </aside>
          </div>

          <div className={`BKIgSheet ${igSheetOpen ? 'BKIgSheet--open' : ''}`}>
            <button
              className="BKIgSheetBar"
              type="button"
              onClick={() => setIgSheetOpen((v) => !v)}
              aria-expanded={igSheetOpen ? 'true' : 'false'}
            >
              <div style={{ textAlign: 'left' }}>
                <div className="BKIgSheetTitle">
                  {igEditTarget ? t('brandKit.pack.liveEditNow') : t('brandKit.pack.liveEditPick')}
                </div>
                <div className="BKIgSheetSub">
                  {igEditTarget ? labelForIgEditTarget(igEditTarget) : t('brandKit.pack.liveInspectorEmpty')}
                </div>
              </div>
              <div className="BKIgSheetChevron" aria-hidden="true">
                {igSheetOpen ? '▾' : '▴'}
              </div>
            </button>
            <div className="BKIgSheetBody">
              {igEditTarget ? renderIgEditor() : <div className="InlineHint">{t('brandKit.pack.liveInspectorEmpty')}</div>}
            </div>
          </div>
        </>
      ) : null}

      {step === 3 ? (
        <div className="Card">
          <div className="Card-title">{t('brandKit.export.title')}</div>
          <div className="Card-body">
            {assets.length ? (
              <>
                <div className="AssetGrid">
                  {assets.map((a) => (
                    <div key={a.id} className="AssetCard">
                      <div className="AssetThumb">
                        {a.kind === 'caption' ? (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              display: 'grid',
                              placeItems: 'center',
                              fontWeight: 900,
                              color: 'rgba(233,238,252,0.92)',
                              background: 'rgba(255,255,255,0.05)',
                              border: '1px solid rgba(255,255,255,0.10)',
                              borderRadius: 12,
                            }}
                          >
                            TXT
                          </div>
                        ) : (
                          <img src={a.dataUrl} alt={a.label} />
                        )}
                      </div>
                      <div className="AssetMeta">
                        <div className="AssetLabel">{a.kind === 'avatar' ? a.label : a.label}</div>
                        <div className="Row Row--tight" style={{ justifyContent: 'space-between' }}>
                          <button className="Btn Btn--ghost" type="button" onClick={() => downloadAsset(a)}>
                            {t('common.download')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="Row Row--tight" style={{ marginTop: 12 }}>
                  <button className="Btn" type="button" onClick={downloadAllAssets}>
                    {t('brandKit.export.exportAll')}
                  </button>
                  <button className="Btn Btn--ghost" type="button" onClick={() => setStep(3)}>
                    {t('common.back')}
                  </button>
                </div>
              </>
            ) : (
              <div className="InlineHint">{t('brandKit.export.empty')}</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
